import { Form, Formik, useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

import { FormLayoutProvider } from 'src/providers/FormLayoutProvider'
import { PaymentProvider } from 'src/providers/PaymentProvider'
import { Step, StepperProvider } from 'src/providers/StepperProvider'
import { useUser } from 'src/providers/UserProvider'

import { Card } from 'src/components/atoms/Card'
import { ExternalLink } from 'src/components/atoms/Link'
import { Heading1, Paragraph } from 'src/components/atoms/Typography'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import { useGetRegistryByIdLazyQuery } from 'src/config/generated/graphql'
import { useCache } from 'src/hooks/useCache'
import { usePackages } from 'src/hooks/usePackages'
import { useProductPrice } from 'src/hooks/useProductPrice'
import { PaymentMethod } from 'src/types/purchase'
import { difference, formatDate } from 'src/utils/date'
import { parseStepIndex } from 'src/utils/stepper'

import { fundingQuantityOptions } from './helper'
import { Payment } from './steps/Payment'
import { Trees } from './steps/Trees'
import { DonateFormValues, DonateFrequencyTab } from './types'
import {
  treeStepSchema,
  paymentStepSchema,
  MessageType
} from './validationSchema'

interface DonateFormProps {
  registryId?: string
}

// Hoisted from the Trees step so the form has the live `productPrice` (and
// the derived `Amount` + `PricePerTree` form fields) regardless of which step
// mounts first. Required for the package flow where /get-started jumps the
// user directly to step 1 (Payment) — without this, PricePerTree stays at
// '0' because the Trees step's effect never runs and Payment shows $0.00.
//
// Renders nothing; lives inside <Formik> so it has access to Formik context.
const PriceSyncEffect = () => {
  const router = useRouter()
  const { values, setFieldValue } = useFormikContext<DonateFormValues>()
  const { productPrice: legacyPrice, getProductPrice } = useProductPrice(
    undefined,
    values.DonationType === DonateFrequencyTab.Once
  )
  const { findBySlug } = usePackages()

  // 0 when the user hasn't selected a quantity yet (Quantity + QuantityOther
  // both blank). The "Amount" form field only fills once this is positive,
  // so the Trees step keeps showing "$0.00" until a quantity is chosen.
  const quantity = useMemo(() => {
    const raw = values.QuantityOther || values.Quantity
    if (!raw) return 0
    const parsed = parseInt(raw, 10)
    return parsed > 0 ? parsed : 0
  }, [values.Quantity, values.QuantityOther])

  // ?package=<slug> on the URL means the visitor arrived via the package flow
  // (`/get-started → /donate/step/1`). When present, the per-tree rate comes
  // from the matching Package's monthly/annual ProductPrice link — NOT the
  // legacy donate product. Without this branch the user would be charged the
  // legacy $10/tree regardless of which tier they "bought".
  const packageSlug =
    typeof router.query.package === 'string' ? router.query.package : undefined
  // donationType=annual on the URL means an annual package subscription. Read
  // it from router.query here (rather than values.DonationType) so the price
  // calc is correct on the first render, before Formik hydrates the form value.
  const isAnnual = router.query.donationType === DonateFrequencyTab.Annual

  const packagePrice = useMemo(() => {
    if (!packageSlug) return undefined
    const pkg = findBySlug(packageSlug)
    if (!pkg) return undefined
    const linked = isAnnual ? pkg.AnnualProductPrice : pkg.MonthlyProductPrice
    return linked?.Amount
  }, [packageSlug, isAnnual, findBySlug])

  const productPrice = packagePrice ?? legacyPrice

  // Only run the legacy fetch when NOT in a package flow. Package pricing
  // comes from usePackages which is already running its own query.
  useEffect(() => {
    if (packageSlug) return
    getProductPrice(quantity > 0 ? quantity : 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, packageSlug])

  // PricePerTree always mirrors the live productPrice — it powers both the
  // per-tree display AND the rate-at-purchase value stored on the
  // Subscription row. Amount only fills once the user has chosen a quantity.
  useEffect(() => {
    if (!productPrice) return
    if (Number(values.PricePerTree) !== productPrice) {
      setFieldValue('PricePerTree', productPrice)
    }
    if (quantity > 0) {
      const nextAmount = (productPrice * quantity).toString()
      if (values.Amount !== nextAmount) {
        setFieldValue('Amount', nextAmount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPrice, quantity, values.Amount, values.PricePerTree])

  return null
}

export const DonateForm = ({ registryId }: DonateFormProps) => {
  const router = useRouter()

  const {
    quantity: quantityFromRouter,
    // Donate flow defaults to Monthly; registry gifts (tabs hidden) stay one-off.
    donationType = registryId
      ? DonateFrequencyTab.Once
      : DonateFrequencyTab.Monthly,
    regionId: regionIdFromRouter,
    stepIndex: stepIndexFromRouter
  } = router.query as {
    quantity: string
    donationType: string
    regionId?: string
    stepIndex: string
  }

  const { productPrice: legacyProductPrice } = useProductPrice(
    Number.parseInt(quantityFromRouter, 10),
    donationType === DonateFrequencyTab.Once
  )

  // Package flow (?package=<slug>): the per-tree rate comes from the tier's
  // monthly/annual ProductPrice, not the legacy product. This MUST be folded in
  // here (not only in PriceSyncEffect) because `cacheOrRouterValues` below seeds
  // the form's initial Amount/PricePerTree — with `enableReinitialize` on, a
  // legacy-priced seed would clobber the tier price the effect sets.
  const { findBySlug } = usePackages()
  const packageSlug =
    typeof router.query.package === 'string' ? router.query.package : undefined
  const isAnnual = donationType === DonateFrequencyTab.Annual
  const packagePrice = useMemo(() => {
    if (!packageSlug) return undefined
    const pkg = findBySlug(packageSlug)
    const linked = isAnnual ? pkg?.AnnualProductPrice : pkg?.MonthlyProductPrice
    return linked?.Amount
  }, [packageSlug, isAnnual, findBySlug])
  const productPrice = packagePrice ?? legacyProductPrice

  const steps: Step[] = useMemo(
    () => [
      { name: 'Trees', component: Trees, validationSchema: treeStepSchema },
      {
        name: 'Payment',
        component: Payment,
        validationSchema: paymentStepSchema
      }
    ],
    []
  )

  const { user } = useUser()

  const initialValues = useMemo<DonateFormValues>(
    () => ({
      Amount: '0',
      InvoiceAddress: {
        InvoiceAddress0: '',
        InvoiceAddress1: '',
        InvoiceCity: '',
        InvoicePostCode: '',
        InvoiceCountry: 'New Zealand'
      },
      PaymentMethod: user?.Account?.PaymentMethodDescriptor
        ? ''
        : PaymentMethod.CreditCard,
      SavePaymentMethod: false,
      // Seed PlantingRegionId from the URL when present so the
      // /get-started → /donate/step/1 package flow preserves the region the
      // user chose on Get Started (the Trees-step picker never runs in that
      // path). SelectPlantingRegion mirrors the same — when a region is
      // explicitly in the URL, treat it as the user's expressed preference.
      PlantingRegionId: regionIdFromRouter || 'choose_for_me',
      Quantity: '5',
      QuantityOther: quantityFromRouter,
      ReceiptEmail: '',
      ReceiptName: '',
      Organisation: '',
      CommsOptIn: false,
      RegistryId: '',
      PublicName: '',
      PublicMessage: '',
      UserEventMessageType: MessageType.None,
      PricePerTree: '0',
      DonationType: donationType,
      PostMessage: false,
      SelectPlantingRegion: !!regionIdFromRouter
    }),
    [
      donationType,
      quantityFromRouter,
      user?.Account?.PaymentMethodDescriptor,
      regionIdFromRouter
    ]
  )

  const isDonatingToRegistry = useMemo(() => !!registryId, [registryId])

  const [getRegistryById, { data: registryData }] =
    useGetRegistryByIdLazyQuery()

  const { value: donateFormCache, setValue: setDonateFormCache } = useCache({
    key: 'donateForm'
  })
  useEffect(() => {
    if (quantityFromRouter && donationType && stepIndexFromRouter) {
      setDonateFormCache(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isDonatingToRegistry) getRegistryById({ variables: { id: registryId } })
  }, [getRegistryById, isDonatingToRegistry, registryId])

  const daysToGo = registryData?.Registry_by_pk?.ClosesOn
    ? difference({ date1: registryData?.Registry_by_pk?.ClosesOn, isUtc: true })
    : null

  const quantityFields = useMemo(() => {
    // If there's a corresponding radio button for the quantity - select this, otherwise set the "Other" field
    if (quantityFromRouter) {
      if (fundingQuantityOptions.find(q => q.value === quantityFromRouter)) {
        return { Quantity: quantityFromRouter }
      }
      return {
        Quantity: 'other',
        QuantityOther: quantityFromRouter
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityFromRouter])

  const stepIndex = parseStepIndex(steps.length, stepIndexFromRouter)

  const amount = useMemo(() => {
    if (
      productPrice &&
      (quantityFields?.Quantity || quantityFields?.QuantityOther)
    ) {
      const quantity = parseInt(
        quantityFields.QuantityOther || quantityFields.Quantity || '1',
        10
      )
      const newQuantity = quantity > 0 ? quantity : 0
      const newPrice = productPrice * newQuantity
      return newPrice
    }
  }, [quantityFields, productPrice])

  const cacheOrRouterValues = useMemo<
    Partial<DonateFormValues> | undefined
  >(() => {
    if (
      amount &&
      productPrice &&
      stepIndexFromRouter &&
      quantityFields &&
      donationType
    ) {
      return {
        ...quantityFields,
        DonationType: donationType,
        Amount: String(amount || ''),
        PricePerTree: String(productPrice || '')
      }
    }
    return donateFormCache
  }, [
    amount,
    productPrice,
    stepIndexFromRouter,
    quantityFields,
    donationType,
    donateFormCache
  ])

  const hydratedInitialValues = useMemo<DonateFormValues>(() => {
    const treesUserHydrationValues: Pick<DonateFormValues, 'PublicName'> | {} =
      user
        ? {
            PublicName: user?.Account?.Funder?.Name
          }
        : {}

    const paymentUserHydrationValues:
      | Pick<DonateFormValues, 'ReceiptName' | 'ReceiptEmail'>
      | {} = user
      ? {
          ReceiptEmail: user?.Account?.Email,
          ReceiptName: user?.Account?.Name
        }
      : {}

    if (stepIndexFromRouter === undefined) {
      return {
        ...initialValues,
        ...treesUserHydrationValues,
        ...quantityFields,
        RegistryId: registryId
      }
    }

    const paymentValues = {
      ...initialValues,
      ...paymentUserHydrationValues,
      ...cacheOrRouterValues,
      ...quantityFields,
      RegistryId: registryId
    }
    return paymentValues
  }, [
    user,
    stepIndexFromRouter,
    initialValues,
    cacheOrRouterValues,
    quantityFields,
    registryId
  ])

  // todo can we find a way to architect the components so we can pull the current step out of the stepper provider
  const validationSchemaForStep = useMemo(() => {
    const step = steps[stepIndex]!
    return step.validationSchema
  }, [stepIndex, steps])

  const headingComponent = useMemo(() => {
    if (!stepIndex || stepIndex !== steps.length - 1)
      return isDonatingToRegistry ? (
        <>
          <Heading1 className='text-center font-bold'>
            Your native tree{' '}
            {registryData?.Registry_by_pk?.IsCampaign ? 'donation' : 'gift'}!
          </Heading1>
          <Paragraph className='text-center text-shark! font-medium'>
            {registryData?.Registry_by_pk?.Name}
          </Paragraph>
          {!registryData?.Registry_by_pk?.IsCampaign ? (
            <Paragraph className='text-center text-shark! font-medium'>
              Registry created by{' '}
              {registryData?.Registry_by_pk?.Funder.Account?.Name} on the{' '}
              {formatDate({
                date: registryData?.Registry_by_pk?.CreatedOn!,
                format: 'Do MMMM YYYY',
                isUtc: true
              })}
              .{daysToGo ? ` This registry ends in ${daysToGo} days.` : null}
            </Paragraph>
          ) : null}
        </>
      ) : (
        <>
          <div className='text-center'>
            <TypographyRebrand variant='h2' color='forestDeep'>
              <strong>
                {user?.Account?.BusinessType ? 'Fund' : 'Donate'} native trees
              </strong>
            </TypographyRebrand>
          </div>
          <div className='text-center'>
            <TypographyRebrand variant='introduction' color='forestDeep'>
              Your native tree donation creates a lasting impact, supporting
              restoration projects across Aotearoa.
            </TypographyRebrand>
          </div>
        </>
      )
    return (
      <div className='text-center'>
        <TypographyRebrand variant='h2' color='forestDeep'>
          <strong>Make payment</strong>
        </TypographyRebrand>
      </div>
    )
  }, [
    daysToGo,
    isDonatingToRegistry,
    registryData?.Registry_by_pk,
    stepIndex,
    steps.length,
    user?.Account?.BusinessType
  ])

  return (
    <>
      <div className='md:mt-12 md:mb-10'>{headingComponent}</div>
      <Card className='mb-5'>
        <Formik
          enableReinitialize
          initialValues={hydratedInitialValues}
          onSubmit={() => {}}
          validationSchema={validationSchemaForStep}
        >
          {({ values, resetForm }) => (
            <PaymentProvider
              key={`${values.DonationType}-${values.SavePaymentMethod}`}
              amount={values.Amount}
              savePaymentMethod={values.SavePaymentMethod}
              isRecurring={
                values.DonationType === DonateFrequencyTab.Monthly ||
                values.DonationType === DonateFrequencyTab.Annual
              }
            >
              <Form>
                <PriceSyncEffect />
                <FormLayoutProvider margin='small'>
                  <StepperProvider
                    steps={steps}
                    onReset={() => resetForm({ values: hydratedInitialValues })}
                    showStepSelector={false}
                  />
                </FormLayoutProvider>
              </Form>
            </PaymentProvider>
          )}
        </Formik>
      </Card>
      <div className='md:mb-24 text-center'>
        <TypographyRebrand variant='paragraphSmall' color='forestDeep'>
          Secure payment. This site is protected by reCAPTCHA and the Google{' '}
          <ExternalLink
            href='https://policies.google.com/privacy'
            color='black-underlined'
            className='text-forestDeep!'
          >
            Privacy Policy
          </ExternalLink>{' '}
          and{' '}
          <ExternalLink
            href='https://policies.google.com/terms'
            color='black-underlined'
            className='text-forestDeep!'
          >
            Terms of Service
          </ExternalLink>{' '}
          apply.
        </TypographyRebrand>
      </div>
    </>
  )
}
